import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { NotificationService } from 'src/app/services/notification.service';
import { extractApiErrorMessage } from 'src/app/shared/utils/extract-api-error-message.util';
import { City, Warehouse } from 'src/typings';
import { WarehouseService } from '../warehouse.service';

@Component({
  selector: 'app-warehouse-card',
  templateUrl: './warehouse-card.component.html',
  styleUrls: ['./warehouse-card.component.scss']
})
export class WarehouseCardComponent implements OnInit {
  warehouseId?: number;

  warehouse?: Warehouse;

  loading = false;

  saving = false;

  warehouseForm = this.fb.group({
    description: new UntypedFormControl('', [Validators.required]),
    city: new UntypedFormControl(null, [Validators.required])
  });

  constructor(
    private fb: UntypedFormBuilder,
    private warehouseService: WarehouseService,
    private route: ActivatedRoute,
    private router: Router,
    private notificationService: NotificationService
  ) {}

  async ngOnInit() {
    const id = Number(this.route.snapshot.paramMap.get('id') || 0);
    if (!id) return;

    this.warehouseId = id;
    await this.loadWarehouse();
  }

  get pageTitle() {
    return this.warehouseId ? 'Редактирование склада' : 'Создание склада';
  }

  get cityControl() {
    return this.warehouseForm.controls.city as UntypedFormControl;
  }

  async submit() {
    this.warehouseForm.markAllAsTouched();
    const description = String(this.warehouseForm.value.description || '').trim();
    const city = this.warehouseForm.value.city as City | string | null;

    if (!description || !city || typeof city === 'string' || !city.id) {
      this.notificationService.warning('Укажите название и выберите город из списка');
      return;
    }

    this.saving = true;
    try {
      const warehouses = await firstValueFrom(this.warehouseService.getAllList());
      const duplicate = warehouses.some(
        (warehouse) =>
          warehouse.id !== this.warehouseId &&
          warehouse.description.trim().toLowerCase() === description.toLowerCase()
      );
      if (duplicate) {
        this.notificationService.warning(`Склад "${description}" уже существует`);
        return;
      }

      if (this.warehouseId) {
        await firstValueFrom(this.warehouseService.update(this.warehouseId, description, city.id));
        this.notificationService.notice('Склад обновлен');
      } else {
        await firstValueFrom(this.warehouseService.create(description, city.id));
        this.notificationService.notice('Склад создан');
      }
      await this.router.navigateByUrl('/warehouse/list');
    } catch (error) {
      this.notificationService.warning(extractApiErrorMessage(error, 'Не удалось сохранить склад'));
    } finally {
      this.saving = false;
    }
  }

  private async loadWarehouse() {
    this.loading = true;
    try {
      const warehouse = await firstValueFrom(
        this.warehouseService.getOne(this.warehouseId as number)
      );
      if (!warehouse) throw new Error('Склад не найден');

      this.warehouse = warehouse;
      this.warehouseForm.patchValue({
        description: warehouse.description,
        city: warehouse.city || null
      });
    } catch (error) {
      this.notificationService.warning(extractApiErrorMessage(error, 'Не удалось загрузить склад'));
      await this.router.navigateByUrl('/warehouse/list');
    } finally {
      this.loading = false;
    }
  }
}
